'use client'

import Link from 'next/link'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { type SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { makeAbsolute } from '@/utils/url'
import { useFormatter } from '@/utils/useFormatter'
import { StoryblokComponent, storyblokEditable } from '@storyblok/react'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { BLOG_ARTICLE_CONTENT_TYPE } from '../blog.constants'
import { convertToBlogArticleCategory } from '../blog.helpers'
import type { BlogArticleCategory } from '../blog.types'
import { type BlogArticleContentType } from '../blog.types'
import { content } from './BlogArticleBlock.css'

type Props = SbBaseBlockProps<BlogArticleContentType['content']>

export const BlogArticleBlock = (props: Props) => {
  const formatter = useFormatter()
  const categories = getCategories(props.blok.categories)

  return (
    <>
      <GridLayout.Root {...storyblokEditable(props.blok)}>
        <GridLayout.Content className={content} width={{ _: '1', md: '5/6', lg: '2/3', xl: '1/2' }} align="center">
          {categories.length > 0 && (
            <div className={xStack({ gap: 'xxs' })}>
              {categories.map((item) => (
                <Link key={item.id} href={makeAbsolute(item.href)}>
                  <Badge as="span">{item.name}</Badge>
                </Link>
              ))}
            </div>
          )}
          <div className={yStack({ gap: 'lg' })}>
            <Heading as="h1" variant={{ _: 'serif.32', lg: 'serif.56' }}>
              {props.blok.page_heading}
            </Heading>
            <Text size="sm" color="textSecondary" uppercase={true}>
              {formatter.dateFull(new Date(props.blok.date))}
            </Text>
          </div>
        </GridLayout.Content>
      </GridLayout.Root>
      {props.blok.body.map((item) => (
        <StoryblokComponent blok={item} key={item._uid} />
      ))}
    </>
  )
}
BlogArticleBlock.blockName = BLOG_ARTICLE_CONTENT_TYPE

const getCategories = (categories: BlogArticleContentType['content']['categories']) => {
  return categories
    .map((item) => (typeof item === 'string' ? null : convertToBlogArticleCategory(item)))
    .filter(isBlogArticleCategory)
}

const isBlogArticleCategory = (item: BlogArticleCategory | null): item is BlogArticleCategory => {
  return item !== null
}
